import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import FlashlightOnOutlinedIcon from "@mui/icons-material/FlashlightOnOutlined";
import FlashlightOffOutlinedIcon from "@mui/icons-material/FlashlightOffOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";

import { normalizeIsbn, isValidIsbn } from "./isbn";
import { playSuccess, playError, vibrate } from "./feedback";

// Only the formats a book barcode can be (Bookland EAN-13, plus EAN-8 for the
// rare short code). Restricting the hint set makes ZXing both faster and far
// less likely to lock onto a stray QR/Code-128 sticker on the back cover.
const ISBN_HINTS = new Map([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8]],
]);

// PRD §2.1: ignore repeated detections of the same ISBN within this window so a
// camera that re-reads the same barcode 30x/sec fires the callback just once.
const SAME_CODE_DEBOUNCE_MS = 2000;

const BarcodeScanner = ({ onDetected }) => {
  const videoRef = React.useRef(null);
  const controlsRef = React.useRef(null); // ZXing IScannerControls (stop)
  const trackRef = React.useRef(null); // live MediaStreamTrack (torch)
  const lastHitRef = React.useRef({ isbn: "", at: 0 });

  const [phase, setPhase] = React.useState("starting"); // starting|scanning|error
  const [errorMsg, setErrorMsg] = React.useState("");
  const [torchOn, setTorchOn] = React.useState(false);
  const [torchSupported, setTorchSupported] = React.useState(false);
  const [flash, setFlash] = React.useState(null); // "success" | "error" | null
  const flashTimerRef = React.useRef(null);

  // Briefly tint the frame green/red as a visual confirmation that pairs with
  // the audio + haptic cue. Auto-clears so the next scan starts clean.
  const triggerFlash = React.useCallback((kind) => {
    setFlash(kind);
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(null), 450);
  }, []);

  // Forward a detected code to the parent, applying the same-code debounce and
  // the client-side checksum gate so a misread never escapes the scanner.
  const handleHit = React.useCallback(
    (rawText) => {
      const isbn = normalizeIsbn(rawText);
      if (!isValidIsbn(isbn)) return; // bad checksum → keep scanning
      const now = Date.now();
      const { isbn: lastIsbn, at } = lastHitRef.current;
      if (isbn === lastIsbn && now - at < SAME_CODE_DEBOUNCE_MS) return;
      lastHitRef.current = { isbn, at: now };
      triggerFlash("success");
      playSuccess();
      vibrate(60);
      onDetected(isbn);
    },
    [onDetected, triggerFlash]
  );

  React.useEffect(() => {
    let cancelled = false;
    let stream = null;
    const video = videoRef.current;
    const reader = new BrowserMultiFormatReader(ISBN_HINTS);

    // We acquire and attach the camera stream ourselves (instead of letting
    // ZXing's decodeFromConstraints do it) so we control the exact attach/play
    // order. The black-frame bug comes from the <video> never actually playing;
    // here we set the iOS-critical attributes BEFORE assigning srcObject, then
    // play on loadedmetadata, then hand the already-running element to ZXing.
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled || !video) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        // Attributes must be set before/at srcObject assignment for iOS Safari.
        video.setAttribute("playsinline", "true");
        video.setAttribute("muted", "true");
        video.muted = true;
        video.srcObject = stream;

        await new Promise((resolve) => {
          if (video.readyState >= 2) return resolve();
          video.onloadedmetadata = () => resolve();
        });
        await video.play().catch(() => {});
        if (cancelled) return;

        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        const caps = track.getCapabilities?.();
        if (caps && "torch" in caps) setTorchSupported(true);
        setPhase("scanning");

        // ZXing now just reads frames from the element we're already playing.
        controlsRef.current = await reader.decodeFromVideoElement(video, (result) => {
          if (!cancelled && result) handleHit(result.getText());
        });
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        playError();
        vibrate([60, 40, 60]);
        setErrorMsg(
          err?.name === "NotAllowedError" || err?.name === "NotReadableError"
            ? "Camera permission denied or in use. Use image upload below instead."
            : "No camera available. Use image upload below instead."
        );
      }
    };

    start();

    return () => {
      cancelled = true;
      controlsRef.current?.stop?.();
      controlsRef.current = null;
      trackRef.current = null;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
      clearTimeout(flashTimerRef.current);
    };
  }, [handleHit]);

  const toggleTorch = async () => {
    const next = !torchOn;
    try {
      await trackRef.current?.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      setTorchSupported(false); // device lied about support; hide the button
    }
  };

  // Fallback path: decode a still image the user picked (covers no-camera
  // devices and iOS file-picker camera capture). PRD §2.1 / §2.3 "No camera".
  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    try {
      const reader = new BrowserMultiFormatReader(ISBN_HINTS);
      const result = await reader.decodeFromImageUrl(url);
      handleHit(result.getText());
    } catch {
      triggerFlash("error");
      playError();
      vibrate([60, 40, 60]);
      setErrorMsg("Couldn't read a barcode from that image. Try a clearer photo.");
    } finally {
      URL.revokeObjectURL(url);
      event.target.value = ""; // allow re-picking the same file
    }
  };

  return (
    <Stack spacing={1.5} sx={{ alignItems: "center" }}>
      <Box sx={{ position: "relative", width: "100%", maxWidth: 360 }}>
        <Box
          component="video"
          ref={videoRef}
          autoPlay
          muted
          playsInline
          sx={{
            width: "100%",
            aspectRatio: "3 / 4", // portrait-first, one-handed (PRD §2.1)
            objectFit: "cover",
            borderRadius: 2,
            background: "#000",
            display: phase === "error" ? "none" : "block",
          }}
        />
        {phase === "scanning" ? (
          // Pulsing crop overlay — the "targeting rectangle" of PRD §2.1/§2.3.
          <Box
            sx={{
              position: "absolute",
              top: "35%",
              left: "10%",
              width: "80%",
              height: "30%",
              border: "3px solid #22c55e",
              borderRadius: 1.5,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
              animation: "pfsScanPulse 1.2s ease-in-out infinite",
              "@keyframes pfsScanPulse": {
                "0%, 100%": { opacity: 0.55 },
                "50%": { opacity: 1 },
              },
            }}
          />
        ) : null}
        {flash ? (
          // Full-frame green/red wash — the visual half of the success/error cue.
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: 2,
              pointerEvents: "none",
              backgroundColor: flash === "success" ? "rgba(34,197,94,0.45)" : "rgba(239,68,68,0.45)",
              transition: "opacity 0.15s ease",
            }}
          />
        ) : null}
      </Box>

      <Typography sx={{ fontSize: 13, color: "#6b7280", textAlign: "center" }}>
        {phase === "starting" && "Starting camera…"}
        {phase === "scanning" && "Point the rear camera at the book's barcode."}
        {phase === "error" && errorMsg}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: "center" }}>
        {torchSupported ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={torchOn ? <FlashlightOffOutlinedIcon /> : <FlashlightOnOutlinedIcon />}
            onClick={toggleTorch}
          >
            {torchOn ? "Torch off" : "Torch on"}
          </Button>
        ) : null}
        <Button size="small" variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />}>
          Upload image
          <input hidden type="file" accept="image/*" onChange={handleFile} />
        </Button>
      </Stack>
    </Stack>
  );
};

export default BarcodeScanner;
